import React from 'react';

// Display helpers for workforce data. The API returns exact decimal-string minutes and UTC ISO
// timestamps; managers read hours and local day/time.

export const minutesToHours = (minutes) => {
  const n = parseFloat(minutes);
  if (Number.isNaN(n)) return '—';
  const h = Math.floor(n / 60);
  const m = Math.round(n - h * 60);
  return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
};

const asDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

export const fmtDay = (iso) => {
  const d = asDate(iso);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : (iso || '—');
};

export const fmtTime = (iso) => {
  const d = asDate(iso);
  return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (iso || '—');
};

export const shiftMinutes = (s) => {
  const a = asDate(s.start);
  const b = asDate(s.end);
  return a && b ? Math.max(0, (b - a) / 60000) : NaN;
};

export function RollupSummary({ rollup }) {
  const sections = Object.entries(rollup.by_section || {});
  return (
    <div data-testid="workforce-rollup-summary" style={{ marginTop: 16 }}>
      <p>
        <strong>{minutesToHours(rollup.total_minutes)}</strong> approved labour,{' '}
        {rollup.segment_count ?? 0} segment{rollup.segment_count === 1 ? '' : 's'}
        {rollup.week_start && <> · week of {fmtDay(rollup.week_start)}</>}
      </p>
      {sections.length === 0 ? (
        <p className="wf-empty">No approved work segments in this week. Rota shifts count once they are worked and approved.</p>
      ) : (
        <div className="wf-table-wrap">
          <table>
            <thead><tr><th>Section</th><th>Hours</th></tr></thead>
            <tbody>
              {sections.map(([sid, mins]) => (
                <tr key={sid}><td>{sid}</td><td>{minutesToHours(mins)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RotaShifts({ rota }) {
  const shifts = [...(rota.shifts || [])].sort((a, b) => String(a.start).localeCompare(String(b.start)));
  const total = shifts.reduce((t, s) => t + (shiftMinutes(s) || 0), 0);
  return (
    <section data-testid="workforce-rota-shifts" style={{ marginTop: 16 }}>
      <h3>Shifts · week of {fmtDay(rota.week_start)} · {shifts.length} shifts, {minutesToHours(total)}</h3>
      {shifts.length === 0 ? <p className="wf-empty">No shifts on this rota yet.</p> : (
        <div className="wf-table-wrap">
          <table>
            <thead><tr><th>Day</th><th>Employee</th><th>Section</th><th>Time</th><th>Hours</th></tr></thead>
            <tbody>
              {shifts.map((s) => (
                <tr key={s.shift_id}>
                  <td>{fmtDay(s.start)}</td>
                  <td>{s.employee_name || s.employee_id || 'Unassigned'}</td>
                  <td>{s.section_name || s.section_id}</td>
                  <td>{fmtTime(s.start)}–{fmtTime(s.end)}</td>
                  <td>{minutesToHours(shiftMinutes(s))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
